/**
 * Extracts individual dark module top-left coordinates `(col, row)` in module grid units
 * from the `bwip-js` SVG path by sampling module centres using the even-odd winding rule.
 */
export function extractModuleCellsFromSvgPath(
  svgPathD: string,
  moduleSize: number,
  moduleCount: number,
): readonly { readonly col: number; readonly row: number }[] {
  const subpaths = svgPathD
    .split(/z/i)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0);
  const polygons: { readonly x: number; readonly y: number }[][] = [];
  for (const sub of subpaths) {
    const tokens = sub.match(/[ML]|-?\d+(?:\.\d+)?/gi) ?? [];
    const pts: { readonly x: number; readonly y: number }[] = [];
    for (let i = 0; i < tokens.length;) {
      const cmd = tokens[i]?.toUpperCase();
      if (cmd === 'M' || cmd === 'L') {
        const x = Number(tokens[i + 1]);
        const y = Number(tokens[i + 2]);
        if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x, y });
        i += 3;
      } else {
        i += 1;
      }
    }
    if (pts.length >= 3) polygons.push(pts);
  }
  const isPointInsideEvenOdd = (px: number, py: number): boolean => {
    let crossings = 0;
    for (const poly of polygons) {
      for (let i = 0; i < poly.length; i += 1) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        if (!a || !b) continue;
        if (a.y > py !== b.y > py) {
          const xIntersect = a.x + ((py - a.y) * (b.x - a.x)) / (b.y - a.y);
          if (px < xIntersect) crossings += 1;
        }
      }
    }
    return crossings % 2 === 1;
  };
  const cells: { readonly col: number; readonly row: number }[] = [];
  for (let row = 0; row < moduleCount; row += 1) {
    const cy = (row + 0.5) * moduleSize;
    for (let col = 0; col < moduleCount; col += 1) {
      if (isPointInsideEvenOdd((col + 0.5) * moduleSize, cy)) cells.push({ col, row });
    }
  }
  return cells;
}

/**
 * Measures the rendered QR symbol geometry directly from the `bwip-js` SVG output.
 */
export function measureQrSvgGeometry(svg: string): {
  readonly viewBoxSize: number;
  readonly moduleSize: number;
  readonly symbolOffset: number;
  readonly quietZoneModules: number;
  readonly symbolModules: number;
  readonly moduleCountWithQuietZone: number;
} {
  const viewBoxMatch = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/.exec(svg);
  const viewBoxSize = viewBoxMatch?.[1] ? Number(viewBoxMatch[1]) : 0;

  const pathMatch = /<path[^>]*\bd="([^"]+)"/.exec(svg);
  const pathD = pathMatch?.[1] ?? '';
  const tokens = pathD.match(/[MLZ]|-?\d+(?:\.\d+)?/gi) ?? [];

  let minCoord = Number.POSITIVE_INFINITY;
  let maxCoord = Number.NEGATIVE_INFINITY;
  let minPositiveDelta = Number.POSITIVE_INFINITY;
  let prevX: number | null = null;
  let prevY: number | null = null;

  let idx = 0;
  while (idx < tokens.length) {
    const cmd = tokens[idx]?.toUpperCase();
    if (cmd === 'M' || cmd === 'L') {
      const x = Number(tokens[idx + 1]);
      const y = Number(tokens[idx + 2]);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        if (x < minCoord) minCoord = x;
        if (y < minCoord) minCoord = y;
        if (x > maxCoord) maxCoord = x;
        if (y > maxCoord) maxCoord = y;
        if (prevX !== null && prevY !== null) {
          const dx = Math.abs(x - prevX);
          const dy = Math.abs(y - prevY);
          if (dx > 0 && dx < minPositiveDelta) minPositiveDelta = dx;
          if (dy > 0 && dy < minPositiveDelta) minPositiveDelta = dy;
        }
        prevX = x;
        prevY = y;
      }
      idx += 3;
    } else {
      prevX = null;
      prevY = null;
      idx += 1;
    }
  }

  const moduleSize =
    Number.isFinite(minPositiveDelta) && minPositiveDelta > 0 ? minPositiveDelta : 4;
  const symbolOffset = Number.isFinite(minCoord) ? minCoord : 0;
  const quietZoneModules = symbolOffset / moduleSize;
  const symbolModules =
    Number.isFinite(maxCoord) && Number.isFinite(minCoord)
      ? Math.round((maxCoord - minCoord) / moduleSize)
      : 0;
  const moduleCountWithQuietZone = Math.round(viewBoxSize / moduleSize);

  return {
    viewBoxSize,
    moduleSize,
    symbolOffset,
    quietZoneModules,
    symbolModules,
    moduleCountWithQuietZone,
  };
}

function convertSvgPathToPdfVectorStream(
  pathD: string,
  viewBoxSize: number,
  pageSizePt: number,
): string {
  const scale = pageSizePt / viewBoxSize;
  const tokens = pathD.match(/[MLZ]|-?\d+(?:\.\d+)?/gi) ?? [];
  const pdfOps: string[] = ['q', '0 0 0 rg'];

  let idx = 0;
  while (idx < tokens.length) {
    const cmd = tokens[idx]?.toUpperCase();
    if (cmd === 'M' || cmd === 'L') {
      const rawX = Number(tokens[idx + 1]);
      const rawY = Number(tokens[idx + 2]);
      if (Number.isFinite(rawX) && Number.isFinite(rawY)) {
        const ptX = (rawX * scale).toFixed(4);
        const ptY = ((viewBoxSize - rawY) * scale).toFixed(4);
        pdfOps.push(`${ptX} ${ptY} ${cmd === 'M' ? 'm' : 'l'}`);
      }
      idx += 3;
    } else if (cmd === 'Z') {
      pdfOps.push('h');
      idx += 1;
    } else {
      idx += 1;
    }
  }

  pdfOps.push('f*', 'Q');
  return pdfOps.join('\n');
}

export function buildVectorPdfDocument(params: {
  readonly url: string;
  readonly clauseRef: string;
  readonly xDimensionMm: number;
  readonly physicalSizeMm: number;
  readonly viewBoxSize: number;
  readonly svgPathD: string;
}): string {
  const pagePt = Number(((params.physicalSizeMm * 72) / 25.4).toFixed(4));
  const streamContent = convertSvgPathToPdfVectorStream(
    params.svgPathD,
    params.viewBoxSize,
    pagePt,
  );
  const streamLength = new TextEncoder().encode(streamContent).length;

  const objects: readonly string[] = [
    `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj`,
    `2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj`,
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${String(pagePt)} ${String(pagePt)}] /Contents 4 0 R /Resources << >> >>\nendobj`,
    `4 0 obj\n<< /Length ${String(streamLength)} >>\nstream\n${streamContent}\nendstream\nendobj`,
    `5 0 obj\n<< /Title (GS1 Digital Link QR - ${params.url}) /Subject (${params.clauseRef}; X=${params.xDimensionMm.toFixed(3)}mm; QZ=4X) /Producer (MyPlayer GS1 Resolver bwip-js gs1dlqrcode) >>\nendobj`,
  ];

  let body = '%PDF-1.4\n';
  const offsets: number[] = [0];

  for (const obj of objects) {
    offsets.push(new TextEncoder().encode(body).length);
    body += `${obj}\n`;
  }

  const xrefOffset = new TextEncoder().encode(body).length;
  let xref = `xref\n0 ${String(objects.length + 1)}\n0000000000 65535 f \n`;
  for (let i = 1; i <= objects.length; i += 1) {
    const offset = offsets[i] ?? 0;
    xref += `${String(offset).padStart(10, '0')} 00000 n \n`;
  }

  const trailer = `trailer\n<< /Size ${String(objects.length + 1)} /Root 1 0 R /Info 5 0 R >>\nstartxref\n${String(xrefOffset)}\n%%EOF\n`;
  return body + xref + trailer;
}
